/**
 * Messages Store - Actions with UnifiedDB v2 Integration
 * 
 * Enhanced actions with offline-first capabilities:
 * - UnifiedDB v2 for message storage
 * - Media blob caching with Cache API
 * - Automatic sync with conflict resolution
 * - Background sync integration
 * 
 * @version 2.0.0
 */

import { useMessagesApi } from '../../composables/useMessagesApi';
import { useOfflineStorage } from '../../composables/useOfflineStorage';
import { logger, getCurrentUserId } from '../../utils';
import type { SendMessagePayload, MessageReadEventData, MessagesStore, OnlineStatus } from './types';
import type { NormalisedMessage } from '../../types/messages';
import {
  normalizeConversation,
  normalizeMessage,
  normalizeUnreadCounters,
  mergeMessagesWithDedup,
  updateMessagesHeaderBadge,
  updateConversationOrder,
  updateConversationAndMoveToTop,
  generateTempId,
  pickHigherStatus,
} from './utils';

// Extended MessagesStore type with offline storage properties
interface MessagesStoreWithOffline extends MessagesStore {
  _offlineStorage: ReturnType<typeof useOfflineStorage> | null;
  _api: ReturnType<typeof useMessagesApi> | null;
  initOfflineStorage(): Promise<void>;
  getApi(): ReturnType<typeof useMessagesApi>;
  getCurrentUserId(): string | number | null;
  syncPendingMessages(): Promise<{ success: boolean; stats: any; error?: string }>;
  processConversationsResponse(response: any): { map: Record<string, ReturnType<typeof normalizeConversation>>; orderedList: ReturnType<typeof normalizeConversation>[] };
  processMessageCounters(response: any): void;
}

export const createActionsWithOffline = () => ({
  // ===== Dependencies (set during store initialization) =====
  
  _offlineStorage: null as ReturnType<typeof useOfflineStorage> | null,
  _api: null as ReturnType<typeof useMessagesApi> | null,

  // Initialize offline storage
  async initOfflineStorage(this: MessagesStoreWithOffline) {
    if (!this._offlineStorage) {
      this._offlineStorage = useOfflineStorage();
      await this._offlineStorage.init();
    }
    if (!this._api) {
      this._api = useMessagesApi();
    }
  },

  // Helper to get API instance
  getApi(this: MessagesStoreWithOffline) {
    if (!this._api) {
      this._api = useMessagesApi();
    }
    return this._api;
  },

  // Helper to get current user ID
  getCurrentUserId(this: MessagesStoreWithOffline) {
    return getCurrentUserId();
  },

  // Helper to process conversations API response
  processConversationsResponse(this: MessagesStoreWithOffline, response: any) {
    const rawConversations =
      response?.conversations_list ??
      response?.conversationsList ??
      response?.conversations ??
      [];
    const list = Array.isArray(rawConversations) ? rawConversations : Object.values(rawConversations);
    const map: Record<string, ReturnType<typeof normalizeConversation>> = {};
    const orderedList: ReturnType<typeof normalizeConversation>[] = [];

    (list || []).forEach((c) => {
      const normalized = normalizeConversation(c);
      const key = normalized?.id;
      if (key === undefined || key === null) return;

      const strKey = String(key);
      // Дедупликация: только если еще не добавлен в orderedList
      if (!map[strKey]) {
        map[strKey] = normalized;
        orderedList.push(normalized);
      } else {
        // Обновляем существующую запись более свежими данными
        map[strKey] = { ...map[strKey], ...normalized };
      }
    });

    this.conversations = map;
    this.conversationOrder = orderedList;
    return { map, orderedList };
  },

  // Helper to process message counters and update badges
  processMessageCounters(this: MessagesStoreWithOffline, response: any) {
    const newMessagesCounters =
      response?.new_messages_counters ??
      response?.newMessagesCounters ??
      response?.new_messages_counter ??
      response?.newMessagesCounter ??
      response?.new_messages ??
      response?.newMessages ??
      {};
    this.unread = normalizeUnreadCounters(newMessagesCounters as any);
    updateMessagesHeaderBadge(this.unread);

    Object.keys(this.unread).forEach((contactId: string) => {
      this.updateSidebarBadgeState(contactId, this.unread[contactId]);
    });
  },

  // ===== Object URL Management =====

  cleanupObjectUrls(this: MessagesStoreWithOffline, urls?: string[]) {
    const urlsToCleanup = urls || Array.from(this.objectUrls);
    urlsToCleanup.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
        this.objectUrls.delete(url);
      } catch (e) {
        logger.warn('Failed to revoke Object URL:', url, e);
      }
    });
  },

  trackObjectUrl(this: MessagesStoreWithOffline, url: string) {
    this.objectUrls.add(url);
  },

  // ===== UI Helpers =====

  updateSidebarBadgeState(this: MessagesStoreWithOffline, contactId: string | number, count: number) {
    if (!contactId) return;

    const key = String(contactId);
    const badge = document.querySelector(`.sidebar-unread-badge[data-contact-id="${key}"]`) as HTMLElement | null;
    if (!badge) return;

    const previous = parseInt(badge.textContent || '0') || 0;
    badge.textContent = String(count);

    if (count > 0 && count !== previous) {
      badge.classList.remove('sidebar-unread-badge-pulse');
      void badge.offsetWidth;
      badge.classList.add('sidebar-unread-badge-pulse');

      const removeHandler = () => {
        badge.classList.remove('sidebar-unread-badge-pulse');
        badge.removeEventListener('animationend', removeHandler);
      };
      badge.addEventListener('animationend', removeHandler);
    }
  },

  // ===== Data Loading with Offline Support =====

  async loadConversations(this: MessagesStoreWithOffline, query = '') {
    this.loading = true;
    
    try {
      // Initialize offline storage
      await this.initOfflineStorage();
      
      const api = this.getApi();
      const response = await api.getConversations(query);

      const { orderedList } = this.processConversationsResponse(response);

      // Cache conversations in IndexedDB with sorting fields
      if (this._offlineStorage) {
        for (const conv of orderedList) {
          try {
            await (window.unifiedDBv2 as any).put('conversations', {
              ...conv,
              id: String(conv.id),
              ownerUserId: this.getCurrentUserId(),
              lastMessageAt: conv.last_message?.timestamp || Date.now() / 1000,
              updatedAt: Date.now(),
              cachedAt: Date.now()
            });
          } catch (e) {
            // Silent fail for caching
          }
        }
      }

      this.loadMessageStatuses(orderedList);

      this.processMessageCounters(response);
    } catch (error) {
      logger.error('Store: Failed to load conversations:', error);
      // If offline, try to load from cache
      if (!navigator.onLine && this._offlineStorage) {
        try {
          // Use sorted getConversations() to preserve ordering by lastMessageAt
          const cachedConversations = await (window.unifiedDBv2 as any).getConversations({ limit: 100 });
          if (cachedConversations.length > 0) {
            const map: Record<string, any> = {};
            const orderedList: any[] = [];
            cachedConversations.forEach((conv: any) => {
              const id = String(conv.id);
              // Дедупликация: только если еще не добавлен
              if (!map[id]) {
                map[id] = conv;
                orderedList.push(conv);
              }
            });
            this.conversations = map;
            this.conversationOrder = orderedList;
            logger.log('Store: Loaded', orderedList.length, 'conversations from cache (sorted by lastMessageAt)');
          }
        } catch (cacheErr) {
          logger.warn('Store: Failed to load conversations from cache:', cacheErr);
        }
      }
    } finally {
      this.loading = false;
    }
  },

  ensureBottomPosition(this: MessagesStoreWithOffline, chatId: string | number) {
    if (chatId !== this.currentContactId) return;
    this.scrollRequest++;
    requestAnimationFrame(() => {
      this.scrollRequest++;
    });
  },

  // ===== Message Statuses =====

  loadMessageStatuses(this: MessagesStoreWithOffline, conversations: any[]) {
    for (const conv of conversations) {
      const convKey = String(conv.id);
      if (conv.last_message) {
        const messageId = String(conv.last_message.id);
        const cacheKey = `${convKey}-${messageId}`;
        const cached = this.messageStatusesCache[cacheKey];
        if (cached) {
          conv.last_message.status = cached.status;
          conv.last_message.is_read = cached.is_read;
        }
      }
    }
  },

  async loadMessages(this: MessagesStoreWithOffline, chatId: string | number, cursor?: string | number) {
    logger.log('Store: loadMessages called with', chatId, cursor);

    this.loading = true;
    
    // Initialize offline storage
    await this.initOfflineStorage();
    
    const api = this.getApi();
    
    // Try to load from cache first if offline
    if (!navigator.onLine && this._offlineStorage) {
      const cachedMessages = await this._offlineStorage.getOfflineMessages(String(chatId));
      if (cachedMessages.length > 0) {
        const currentUserId = this.getCurrentUserId();
        const filtered = cachedMessages.filter(m =>
          m.ownerUserId === undefined || String(m.ownerUserId) === String(currentUserId)
        );
        const mapped = filtered.map(m => ({
          ...m,
          chatId: String(chatId),
          ts: m.ts || m.timestamp || Math.floor(Date.now() / 1000),
          is_read: m.is_read || false,
          user: m.user || { id: m.from_user_id || 'unknown' },
          _fromCache: true
        }));
        // Deduplicate: if a temp message has _serverId matching another message's id, keep only the server version
        const serverIdSet = new Set(mapped.filter((m: any) => {
          const idStr = String(m.id ?? '');
          return !idStr.startsWith('temp-') && !idStr.startsWith('temp_');
        }).map((m: any) => String(m.id)));
        const deduped = mapped.filter((m: any) => {
          const idStr = String(m.id ?? '');
          const isTemp = idStr.startsWith('temp-') || idStr.startsWith('temp_');
          if (!isTemp) return true;
          // Drop temp message if server version already exists
          const sid = m._serverId;
          return !sid || !serverIdSet.has(String(sid));
        });
        this.messagesByChat[chatId] = deduped as NormalisedMessage[];
        this.loading = false;
        logger.log('Store: Loaded', deduped.length, 'messages from cache (offline, deduped from', mapped.length, ')');
        return;
      }
    }
    
    let response;
    try {
      response = await api.getMessages(chatId, cursor);
    } catch (error) {
      logger.warn('Store: Failed to fetch messages:', error);
      // If offline and we have no cached messages yet, try cache
      if (!navigator.onLine && this._offlineStorage) {
        const cachedMessages = await this._offlineStorage.getOfflineMessages(String(chatId));
        if (cachedMessages.length > 0) {
          // Filter out soft-deleted messages (backend deletion logic)
          const currentUserId = this.getCurrentUserId();
          const filtered = cachedMessages.filter(m => {
            // Backend uses soft deletion: is_deleted_by_sender and is_deleted_by_receiver
            const isSender = String(m.from_user_id) === String(currentUserId);
            const softDeleteOk = !(isSender && m.is_deleted_by_sender) && !(!isSender && m.is_deleted_by_receiver);
            // Defense-in-depth: filter by ownerUserId
            return softDeleteOk && (m.ownerUserId === undefined || String(m.ownerUserId) === String(currentUserId));
          });
          
          const mapped = filtered.map(m => ({
            ...m,
            chatId: String(chatId),
            ts: m.ts || m.timestamp || Math.floor(Date.now() / 1000),
            is_read: m.is_read || false,
            user: m.user || { id: m.from_user_id || 'unknown' },
            _fromCache: true
          }));
          this.messagesByChat[chatId] = mapped as NormalisedMessage[];
          logger.log('Store: Loaded', mapped.length, 'messages from cache after fetch error (filtered from', cachedMessages.length, 'total)');
        }
      }
      this.loading = false;
      return;
    }

    const rawMessages = response?.messages || {};
    const list = Array.isArray(rawMessages) ? rawMessages : Object.values(rawMessages);
    const normalized = (list || [])
      .map((m) => normalizeMessage(m as Record<string, unknown>))
      .filter((m): m is NonNullable<typeof m> => m !== null)
      .sort((a, b) => (a.ts || a.timestamp || 0) - (b.ts || b.timestamp || 0));

    if (!cursor) {
      const existing = this.messagesByChat[chatId] || [];
      const { merged, updates } = mergeMessagesWithDedup(existing, normalized);

      for (const { tempId, patch } of updates) {
        this.updateMessage(tempId, patch);
      }

      this.messagesByChat[chatId] = merged;

      // Catch-up ack: сообщения, доставленные сервером, но ещё не подтверждённые этим клиентом
      const undeliveredIds = merged
        .filter((m: NormalisedMessage) => m.type === 'received' && m.status === 'sent')
        .map((m: NormalisedMessage) => m.id);
      if (undeliveredIds.length) {
        this.ackDelivered(chatId, undeliveredIds);
      }
      
      // Cache messages in IndexedDB and cleanup stale temp records
      if (this._offlineStorage) {
        // Get ALL messages for this conversation (not just offline ones)
        const allCachedMessages = await (window.unifiedDBv2 as any).getByIndex('messages', 'conversationId', String(chatId));
        const serverMessageIds = new Set(normalized.map(m => String(m.id)));
        
        logger.log('Store: Cached messages count:', allCachedMessages.length, 'Server messages count:', serverMessageIds.size);
        
        // Delete messages from cache that are not in server response (deleted on server)
        // Backend uses soft deletion with is_deleted_by_sender/is_deleted_by_receiver flags
        // Messages with these flags are filtered out by backend's between() query
        for (const cachedMsg of allCachedMessages) {
          const cachedId = String(cachedMsg.id);
          // Skip offline/temp messages (they have _offline or synced=false)
          if (cachedMsg._offline || cachedMsg.synced === false) {
            continue;
          }
          // If message is synced but not in server response, it was deleted on server (soft deletion)
          if (!serverMessageIds.has(cachedId)) {
            try {
              await (window.unifiedDBv2 as any).delete('messages', cachedId);
              logger.log('Store: Cleaned up soft-deleted message from cache:', cachedId, 'text:', cachedMsg.text?.substring(0, 30));
            } catch (e) {
              logger.error('Store: Failed to delete cached message:', cachedId, e);
            }
          }
        }
        
        // Save current server messages to cache with deletion flags
        for (const msg of normalized) {
          try {
            await (window.unifiedDBv2 as any).saveMessage({
              ...msg,
              conversationId: String(chatId),
              synced: true,
              _cached: true,
              // Store deletion flags from backend
              is_deleted_by_sender: msg.is_deleted_by_sender ?? false,
              is_deleted_by_receiver: msg.is_deleted_by_receiver ?? false
            }, { storeMedia: false });
            // If this server message has a temp_id, delete the stale temp record from IndexedDB
            const msgTempId = msg.temp_id ?? msg.tempId ?? msg.client_message_id;
            if (msgTempId) {
              try {
                await (window.unifiedDBv2 as any).delete('messages', String(msgTempId));
              } catch (_) { /* ignore */ }
            }
          } catch (e) {
            // Silent fail for caching
          }
        }
      }
      
      // POST-LOAD CLEANUP: Fuzzy merge any remaining temp/server duplicates
      for (const msg of normalized) {
        const tempKey = (msg.tempId || msg.client_message_id || msg.temp_id) as string | undefined;
        const serverId = msg.id;
        
        if (tempKey && serverId && tempKey !== String(serverId)) {
          const duplicateIdx = this.messagesByChat[chatId].findIndex((m: NormalisedMessage) => 
            String(m.id) === String(serverId) && m.tempId !== tempKey
          );
          if (duplicateIdx >= 0) {
            logger.log('Store: Removing duplicate server message:', serverId);
            const updatedList = [...this.messagesByChat[chatId]];
            updatedList.splice(duplicateIdx, 1);
            this.messagesByChat[chatId] = updatedList;
          }
        }
      }
    }

    this.loading = false;

    // Сохранение контактных данных из API (восстановлено из оригинального actions.ts)
    const contactData = (response as any)?.contact;
    if (contactData?.id) {
      const cid = contactData.id;
      if (!this.conversations[cid]) {
        this.conversations[cid] = { id: cid, contact: contactData } as any;
      } else {
        this.conversations[cid] = { ...this.conversations[cid], contact: contactData } as any;
      }
    }
  },

  // ===== Enhanced Send Message with Offline Support =====

  async sendMessage(this: MessagesStoreWithOffline, payload: SendMessagePayload) {
    await this.initOfflineStorage();
    
    const { text, contactId, tempId: providedTempId, files, attachments, onUploadProgress, displayText } = payload;
    const tempId = providedTempId || generateTempId();
    const currentUserId = this.getCurrentUserId();
    const allFiles = files || attachments || [];

    const currentUserAvatar =
      (window as any).__messagesCurrentUserAvatar ||
      (window as any).currentUser?.avatar ||
      (window as any).currentUser?.photo ||
      null;
    const contactAvatar = this.conversations?.[String(contactId)]?.contact?.avatar || null;
    const finalAvatar = currentUserAvatar || contactAvatar;

    const optimistic: any = {
      id: tempId,
      chatId: contactId,
      contactId,
      text: text || '',
      body: text || '',
      displayText: displayText || text || '',
      attachments: [],
      from_user_id: currentUserId,
      type: 'sent',
      status: 'pending',
      ts: Math.floor(Date.now() / 1000),
      timestamp: Math.floor(Date.now() / 1000),
      _pending: true,
      _deletedLocally: false,
      user: {
        id: currentUserId,
        avatar: finalAvatar,
      },
    };

    if (allFiles.length > 0) {
      optimistic.attachments = await Promise.all(
        allFiles.map(async (file) => {
          const objectUrl = URL.createObjectURL(file);
          this.trackObjectUrl(objectUrl);
          return {
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            objectUrl,
            tempId: `file_${Date.now()}_${Math.random()}`,
            _pending: true,
            _uploadProgress: 0,
          };
        })
      );
      optimistic.hasMedia = true;
    }

    this.addMessage(optimistic);

    try {
      const api = this.getApi();

      // Create progress callback to update message during upload
      const uploadProgressCallback = onUploadProgress || ((percent: number) => {
        this.updateMessage(tempId, {
          chatId: contactId,
          status: 'uploading',
          _uploadProgress: percent,
        });

        // Update attachment progress too
        const msg = this.messagesByChat[contactId]?.find((m: any) =>
          String(m.id) === String(tempId) ||
          String(m._serverId) === String(tempId) ||
          String(m.tempId) === String(tempId)
        );
        if (msg?.attachments?.length) {
          const next = msg.attachments.map((a: any) => ({ ...a, _uploadProgress: percent }));
          this.updateMessage(tempId, { chatId: contactId, attachments: next });
        }
      });

      const result = await api.sendMessage({
        text,
        contactId,
        tempId,
        files: allFiles,
        onUploadProgress: uploadProgressCallback,
      });

      if (result.success) {
        this.updateMessage(tempId, {
          chatId: contactId,
          status: 'sent',
          _pending: false,
          _serverId: result.messageId ?? undefined,
        });

        if (result.method === 'upload' && allFiles.length > 0 && !result.messageId) {
          void this.backgroundUploadFiles(String(tempId), allFiles, contactId);
        }

        if (result.method === 'http') {
          this.updateMessage(tempId, { chatId: contactId, _uploadComplete: true });
          // Don't reload messages after HTTP send - WebSocket will handle server confirmation
          // This prevents animation duplication and scroll issues
          // await this.loadMessages(contactId);

          const convKey = String(contactId);
          const conv = this.conversations[convKey];
          if (conv) {
            conv.last_message = {
              ...(conv.last_message || {}),
              text: displayText || text || '',
              timestamp: Date.now() / 1000,
              type: 'sent',
              status: 'sent',
              is_read: false,
              hasMedia: allFiles.length > 0,
            };
            this.conversations[convKey] = { ...conv };
            updateConversationOrder(this, convKey, conv.last_message);
          }
        }

        return result;
      } else {
        this.updateMessage(tempId, { chatId: contactId, status: 'failed' });

        const objectUrls = optimistic.attachments.filter((att: any) => att.objectUrl).map((att: any) => att.objectUrl);
        if (objectUrls.length > 0) {
          this.cleanupObjectUrls(objectUrls as string[]);
        }

        return result;
      }
    } catch (e) {
      console.error('Send message failed:', e);

      const isNetworkError =
        !navigator.onLine || (e instanceof TypeError && /fetch|network/i.test((e as TypeError).message));

      if (isNetworkError && text && allFiles.length === 0) {
        logger.log('Store: Network error detected, saving message to IndexedDB for retry');
        this.updateMessage(tempId, { chatId: contactId, status: 'pending', _offline: true });
        
        // Use new offline storage
        if (this._offlineStorage) {
          try {
            await this._offlineStorage.saveOfflineMessage({
              id: String(tempId),
              text: String(text || ''),
              conversationId: String(contactId),
              contactId: String(contactId),
              from_user_id: String(currentUserId),
              timestamp: Math.floor(Date.now() / 1000),
              status: 'pending',
              type: 'sent',
              _offline: true
            });
            
            // Queue for sync
            await this._offlineStorage.queueOperation('send_message', {
              text,
              contactId: String(contactId),
              tempId
            }, { priority: 'high' });
            
            // Request background sync
            void this._offlineStorage.requestSync();
            
          } catch (offlineErr) {
            console.warn('Store: Failed to queue message to offline storage:', offlineErr);
          }
        }
        
        return { success: true, method: 'offline-queued', tempId };
      }

      this.updateMessage(tempId, { chatId: contactId, status: 'failed' });

      const objectUrls = optimistic.attachments.filter((att: any) => att.objectUrl).map((att: any) => att.objectUrl);
      if (objectUrls.length > 0) {
        this.cleanupObjectUrls(objectUrls);
      }

      if (window.messenger?.error) {
        window.messenger.error('Не удалось отправить сообщение');
      }

      return { success: false, message: (e as Error).message || 'Send failed' };
    }
  },

  // ===== Enhanced Background Upload with Media Caching =====

  async backgroundUploadFiles(this: MessagesStoreWithOffline, tempId: string, files: File[], contactId: string | number) {
    logger.log('Store: Starting background upload for tempId:', tempId);

    try {
      const api = this.getApi();

      this.updateMessage(tempId, {
        status: 'uploading',
        _backgroundUploadStarted: true,
        _uploadProgress: 0,
      });

      const currentMsg = this.messagesByChat[contactId]?.find((m) => 
        String(m.id) === String(tempId) || 
        String(m._serverId) === String(tempId) ||
        String(m.tempId) === String(tempId)
      );
      
      if (currentMsg?.attachments?.length) {
        const updatedAttachments = currentMsg.attachments.map((a: any) => ({ ...a, _uploadProgress: 0 }));
        this.updateMessage(tempId, { chatId: contactId, attachments: updatedAttachments });
      }

      const uploadResult = await api.uploadImages(files, contactId, tempId, {
        // Текст сопроводительного сообщения — иначе retry создаст медиа-сообщение без текста
        text: (currentMsg?.text as string) || '',
        onProgress: (percent) => {
          logger.log('Store: Upload progress', tempId, percent + '%');
          this.updateMessage(tempId, { chatId: contactId, _uploadProgress: percent });

          const msg = this.messagesByChat[contactId]?.find((m) => 
            String(m.id) === String(tempId) || 
            String(m._serverId) === String(tempId) ||
            String(m.tempId) === String(tempId)
          );
          if (msg?.attachments?.length) {
            const next = msg.attachments.map((a: any) => ({ ...a, _uploadProgress: percent }));
            logger.log('Store: Updating attachment progress', next.map((a: any) => a._uploadProgress));
            this.updateMessage(tempId, { chatId: contactId, attachments: next });
          }
        },
      });

      if (uploadResult.success) {
        logger.log('Store: Background upload successful for tempId:', tempId);

        const objectUrlsToCleanup: string[] = [];
        try {
          const msg = this.messagesByChat[contactId]?.find((m) => 
            String(m.id) === String(tempId) || 
            String(m._serverId) === String(tempId) ||
            String(m.tempId) === String(tempId)
          );
          if (msg?.attachments?.length) {
            msg.attachments.forEach((a: any) => {
              if (a?.objectUrl) objectUrlsToCleanup.push(String(a.objectUrl));
            });
          }
        } catch {
          // ignore
        }

        try {
          const msg = this.messagesByChat[contactId]?.find((m) => 
            String(m.id) === String(tempId) || 
            String(m._serverId) === String(tempId) ||
            String(m.tempId) === String(tempId)
          );
          if (msg?.attachments?.length) {
            const next = msg.attachments.map((a: any) => ({
              ...a,
              _pending: false,
              _uploadProgress: undefined,
            }));
            this.updateMessage(tempId, { chatId: contactId, attachments: next });
          }
        } catch {
          // ignore
        }

        this.updateMessage(tempId, {
          status: 'sent',
          _backgroundUploaded: true,
          _uploadComplete: true,
          _uploadProgress: 100,
          _backgroundUploadStarted: false,
        });

        // Don't reload messages after background upload - WebSocket will handle server confirmation
        // This prevents animation duplication and scroll issues
        // await this.loadMessages(contactId);

        // Trigger scroll to bottom after media upload completes
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('vue-messages-media-uploaded', {
            detail: { contactId, tempId }
          }));
        }

        if (objectUrlsToCleanup.length > 0) {
          setTimeout(() => this.cleanupObjectUrls(objectUrlsToCleanup), 30000);
        }
      } else {
        const errorMessage = uploadResult.message || 'Upload failed';
        console.error('Store: Background upload failed for tempId:', tempId, errorMessage);

        this.updateMessage(tempId, {
          status: 'error',
          _backgroundUploadFailed: true,
          _backgroundUploadStarted: false,
          _error: errorMessage,
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Store: Background upload failed for tempId:', tempId, errorMessage);

      this.updateMessage(tempId, {
        status: 'error',
        _backgroundUploadFailed: true,
        _backgroundUploadStarted: false,
        _error: errorMessage,
      });
    }
  },

  // ===== Enhanced Offline Sync =====

  async syncPendingMessages(this: MessagesStoreWithOffline): Promise<{ success: boolean; stats: any; error?: string }> {
    if (!navigator.onLine) return { success: false, stats: { error: 'offline' } };
    
    await this.initOfflineStorage();
    
    if (!this._offlineStorage) return { success: false, stats: { error: 'offline storage not initialized' } };

    try {
      const result = await this._offlineStorage.syncWithServer();
      
      // Always refresh conversations on sync to get latest data from server
      try {
        await this.loadConversations();
      } catch (convErr) {
        logger.warn('Store: Failed to refresh conversations after sync:', convErr);
      }
      
      if (result.success) {
        // Update synced messages directly without reload to avoid animation
        // Get tempId -> serverId mapping from sync result
        const syncedMessages = (result as any).syncedMessages || [];
        
        for (const synced of syncedMessages) {
          const { tempId, serverId, conversationId } = synced;
          if (tempId && serverId) {
            // Update the message in Vue store: replace tempId with serverId
            // and update status to delivered
            const chatId = conversationId || this.currentContactId;
            if (chatId) {
              const msg = this.messagesByChat[chatId]?.find((m: any) => 
                String(m.id) === String(tempId) || 
                String(m.tempId) === String(tempId)
              );
              
              if (msg) {
                // Update the message with server ID and status
                this.updateMessage(tempId, {
                  chatId,
                  id: serverId,
                  _serverId: serverId,
                  status: 'sent',
                  _pending: false,
                  _offline: false,
                });
                
                // Also update IndexedDB with the server ID
                if (this._offlineStorage) {
                  try {
                    await this._offlineStorage.markMessageSynced(String(tempId), String(serverId));
                  } catch (e) {
                    logger.warn('Store: Failed to mark message as synced in IndexedDB:', e);
                  }
                }
              }
            }
          }
        }
      }
      
      return result;
    } catch (err) {
      console.warn('Store: syncPendingMessages failed:', err);
      return { success: false, stats: { error: err instanceof Error ? err.message : String(err) } };
    }
  },

  async restorePendingMessages(this: MessagesStoreWithOffline, chatId?: string | number) {
    await this.initOfflineStorage();
    
    if (!this._offlineStorage) return;

    try {
      const messages = await this._offlineStorage.restorePendingMessages(chatId ? String(chatId) : undefined);
      const currentUserId = this.getCurrentUserId();
      const currentUserAvatar =
        (window as any).__messagesCurrentUserAvatar ||
        (window as any).currentUser?.avatar ||
        null;

      for (const stored of messages) {
        const cid = stored.conversationId || stored.contactId;
        if (!cid) continue;

        const existing = (this.messagesByChat[String(cid)] || []).find((m: NormalisedMessage) => String(m.id) === String((stored as any).id));
        if (existing) continue;

        const restored: any = {
          id: stored.id,
          chatId: cid,
          text: stored.text || (stored as any).body || '',
          attachments: [],
          from_user_id: stored.from_user_id || currentUserId || 'me',
          type: 'sent',
          status: 'pending',
          ts: stored.timestamp || Math.floor(Date.now() / 1000),
          timestamp: stored.timestamp || Math.floor(Date.now() / 1000),
          _deletedLocally: false,
          _offline: true,
          user: {
            id: stored.from_user_id || currentUserId,
            avatar: currentUserAvatar,
          },
        };

        this.addMessage(restored);

        const convKey = String(cid);
        const conv = this.conversations[convKey];
        if (conv) {
          const existingTs = conv.last_message?.timestamp || 0;
          if (restored.timestamp >= existingTs) {
            conv.last_message = {
              ...(conv.last_message || {}),
              text: (restored.displayText || restored.text || '').toString(),
              timestamp: restored.timestamp,
              type: 'sent',
              status: 'pending',
              is_read: false,
              hasMedia: false,
            };
            this.conversations[convKey] = { ...conv };
            if (conv.last_message) {
              updateConversationOrder(this, convKey, conv.last_message);
            }
          }
        }
      }
    } catch (err) {
      console.warn('Store: Failed to restore pending messages:', err);
    }
  },

  // ===== Draft Operations =====

  async saveDraft(this: MessagesStoreWithOffline, conversationId: string | number, draft: { text: string; attachments?: any[]; replyTo?: any }) {
    await this.initOfflineStorage();
    if (this._offlineStorage) {
      await this._offlineStorage.saveDraft(String(conversationId), draft);
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async loadDraft(this: MessagesStoreWithOffline, conversationId: string | number) {
    await this.initOfflineStorage();
    if (this._offlineStorage) {
      return await this._offlineStorage.getDraft(String(conversationId));
    }
    return null;
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async clearDraft(this: MessagesStoreWithOffline, conversationId: string | number) {
    await this.initOfflineStorage();
    if (this._offlineStorage) {
      await this._offlineStorage.deleteDraft(String(conversationId));
    }
  },

  // ===== Existing methods (unchanged) =====

  setCurrentContactId(this: MessagesStoreWithOffline, contactId: string | number | null) {
    logger.log('Store: setCurrentContactId called with', contactId);
    this.currentContactId = contactId;
    // Notify SW about active chat so it can suppress push for this contact
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'active_chat',
        contactId: contactId ? Number(contactId) : null,
      });
    }
  },

  addMessage(this: MessagesStoreWithOffline, message: Record<string, unknown>) {
    const rawChatId = message.chatId || message.contactId || message.conversationId || message.to_user_id;
    if (!rawChatId) {
      logger.warn('Store: addMessage - no chatId', message);
      return;
    }
    
    const chatId = String(rawChatId);

    if (!this.messagesByChat[chatId]) {
      this.messagesByChat[chatId] = [];
    }

    const normalized = normalizeMessage(message);
    if (!normalized) {
      logger.warn('Store: addMessage - failed to normalize', message);
      return;
    }

    // ... rest of existing addMessage logic
    const tempKey = (normalized.tempId || normalized.client_message_id || normalized.temp_id) as string | undefined;
    const serverId = normalized.id;

    // Check for temp message to merge
    if (tempKey) {
      const idxByTemp = this.messagesByChat[chatId].findIndex((m: NormalisedMessage) => 
        String(m.id) === String(tempKey) ||
        String(m.tempId) === String(tempKey) ||
        String(m.client_message_id) === String(tempKey)
      );
      
      if (idxByTemp >= 0) {
        const existing = this.messagesByChat[chatId][idxByTemp];
        logger.log('Store: Found temp message by tempKey, merging:', tempKey);
        const updatedList = [...this.messagesByChat[chatId]];
        updatedList[idxByTemp] = { 
          ...existing, 
          ...normalized, 
          id: existing.id,  // Keep tempId to prevent Vue animation duplication
          _serverId: serverId,  // Store serverId separately
          status: pickHigherStatus(existing.status, (normalized as any).status || 'sent') ?? 'sent',
          _pending: false
        };
        this.messagesByChat[chatId] = updatedList;
        updateConversationAndMoveToTop(this, chatId, updatedList[idxByTemp] as any);
        return;
      }
    }

    // Check by _serverId
    if (serverId) {
      const idxByServerId = this.messagesByChat[chatId].findIndex((m: NormalisedMessage) => 
        String(m._serverId) === String(serverId) ||
        String(m.id) === String(serverId)
      );
      
      if (idxByServerId >= 0) {
        const existing = this.messagesByChat[chatId][idxByServerId];
        logger.log('Store: Found temp message by _serverId, merging:', serverId);
        const updatedList = [...this.messagesByChat[chatId]];
        
        const mergedAttachments = existing.attachments?.map((att: any) => ({
          ...att,
          _pending: false,
          _uploadProgress: undefined,
        })) || normalized.attachments;
        
        updatedList[idxByServerId] = { 
          ...existing, 
          ...normalized, 
          id: existing.id,  // Keep original id to prevent Vue animation duplication
          _serverId: normalized.id,  // Store serverId separately
          status: pickHigherStatus(existing.status, (normalized as any).status || 'sent') ?? 'sent',
          _uploadComplete: true,
          _backgroundUploaded: true,
          _backgroundUploadStarted: false,
          _pending: false,
          _uploadProgress: undefined,
          attachments: mergedAttachments,
        };
        this.messagesByChat[chatId] = updatedList;
        updateConversationAndMoveToTop(this, chatId, updatedList[idxByServerId] as any);
        return;
      }

      // Check for duplicates by serverId
      const existingByServerId = this.messagesByChat[chatId].findIndex(
        (m: NormalisedMessage) => String(m.id) === String(serverId)
      );
      if (existingByServerId >= 0) {
        logger.log('Store: Message with serverId already exists, skipping duplicate');
        return;
      }
    }

    // FALLBACK FUZZY MATCH
    const currentUserId = this.getCurrentUserId();
    const isOwnMessage = String(normalized.from_user_id) === String(currentUserId);
    const hasAttachments = Array.isArray(normalized.attachments) && normalized.attachments.length > 0;
    
    if (isOwnMessage && hasAttachments && !tempKey && serverId) {
      logger.log('Store: Fuzzy fallback for media message without client_message_id, serverId:', serverId);
      
      const tempMediaMessages = this.messagesByChat[chatId].filter((m: NormalisedMessage) => {
        const idStr = String(m?.id ?? '');
        const isTemp = idStr.startsWith('temp-') || idStr.startsWith('temp_');
        const hasAtt = Array.isArray(m?.attachments) && m.attachments.length > 0;
        const hasNoServerId = !m?._serverId;
        return isTemp && hasAtt && hasNoServerId;
      });
      
      logger.log('Store: Fuzzy fallback candidates:', tempMediaMessages.length);
      
      if (tempMediaMessages.length > 0) {
        const bestCandidate = tempMediaMessages.sort((a: NormalisedMessage, b: NormalisedMessage) => {
          const tsA = Number(a?.timestamp ?? a?.ts ?? 0);
          const tsB = Number(b?.timestamp ?? b?.ts ?? 0);
          return tsB - tsA;
        })[0];
        
        const idx = this.messagesByChat[chatId].findIndex((m) => String(m.id) === String(bestCandidate.id));
        if (idx >= 0) {
          logger.log('Store: Fuzzy fallback match found, merging:', bestCandidate.id);
          const existing = this.messagesByChat[chatId][idx];
          const updatedList = [...this.messagesByChat[chatId]];
          
          const mergedAttachments = existing.attachments?.map((att: any) => ({
            ...att,
            _pending: false,
            _uploadProgress: undefined,
          })) || normalized.attachments;
          
          updatedList[idx] = { 
            ...existing, 
            ...normalized, 
            id: normalized.id,
            _serverId: normalized.id,
            status: pickHigherStatus(existing.status, (normalized as any).status || 'sent') ?? 'sent',
            _uploadComplete: true,
            _backgroundUploaded: true,
            _backgroundUploadStarted: false,
            _pending: false,
            _uploadProgress: undefined,
            attachments: mergedAttachments,
          };
          this.messagesByChat[chatId] = updatedList;
          updateConversationAndMoveToTop(this, chatId, updatedList[idx] as any);
          return;
        }
      }
    }

    // Check for duplicates
    const existingIdx = this.messagesByChat[chatId].findIndex((m) => String(m.id) === String(normalized.id));
    if (existingIdx >= 0) {
      const existing = this.messagesByChat[chatId][existingIdx];
      const updatedList = [...this.messagesByChat[chatId]];
      updatedList[existingIdx] = {
        ...existing,
        ...normalized,
        // Статус не понижается: дубликат может нести устаревший статус
        status: pickHigherStatus(existing.status, (normalized as any).status || 'sent') ?? 'sent',
      };
      this.messagesByChat[chatId] = updatedList;
      updateConversationAndMoveToTop(this, chatId, updatedList[existingIdx] as any);
      return;
    }

    this.messagesByChat[chatId].push(normalized as any);
    updateConversationAndMoveToTop(this, chatId, normalized as any);

    // Cache incoming message to IndexedDB for offline access
    // Only cache messages with real server IDs (not temp/optimistic messages)
    const msgIdStr = String(normalized.id ?? '');
    const isTempMessage = msgIdStr.startsWith('temp-') || msgIdStr.startsWith('temp_');
    if (!isTempMessage && !normalized._offline && typeof window !== 'undefined' && (window as any).unifiedDBv2?.saveMessage) {
      try {
        void (window as any).unifiedDBv2.saveMessage({
          ...normalized,
          conversationId: chatId,
          synced: true,
          _cached: true,
        }, { storeMedia: false });
      } catch (e) {
        // Silent fail for caching
      }
    }

    // Delivery ack: сообщаем серверу, что входящее сообщение доставлено на устройство
    if (normalized.type === 'received' && !isTempMessage && !normalized._offline) {
      this.ackDelivered(chatId, [normalized.id]);
    }

    // Increment unread count
    if (normalized.type === 'received' && String(chatId) !== String(this.currentContactId)) {
      const currentCount = this.unread[chatId] || 0;
      this.unread[chatId] = currentCount + 1;
      updateMessagesHeaderBadge(this.unread);
      this.updateSidebarBadgeState(chatId, this.unread[chatId]);
      logger.log('Store: Incremented unread count for', chatId, 'to', this.unread[chatId]);
    }
  },

  updateMessage(this: MessagesStoreWithOffline, tempId: string | number, patch: Partial<any>) {
    const idStr = String(tempId);
    const rawChatId = patch.chatId || this.currentContactId;
    if (!rawChatId) {
      logger.warn('Store: updateMessage - no chatId', { tempId, patch });
      return;
    }
    const chatId = String(rawChatId);

    const list = this.messagesByChat[chatId];
    if (!list) {
      logger.warn('Store: updateMessage - no list for chatId', { chatId, tempId });
      return;
    }

    const idx = list.findIndex((m: NormalisedMessage) => 
      String(m.id) === idStr || 
      String(m._serverId) === idStr ||
      String(m.tempId) === idStr
    );
    
    if (idx >= 0) {
      const existing = list[idx];
      const safePatch: Partial<any> = { ...patch };
      // Не понижаем статус: событие message_read может опередить подтверждение отправки/эхо
      if (safePatch.status !== undefined) {
        safePatch.status = pickHigherStatus(existing.status, safePatch.status);
      }
      const updatedMessage = { ...existing, ...safePatch };
      const newList = list.map((m: NormalisedMessage, i: number) => 
        i === idx ? updatedMessage : m
      );
      this.messagesByChat = {
        ...this.messagesByChat,
        [chatId]: newList
      };
      logger.log('Store: Message updated, new status:', updatedMessage.status, 'for id:', tempId);
    } else {
      logger.warn('Store: updateMessage - message not found', { tempId, chatId, listLength: list.length });
    }
  },

  // ===== Read Status =====

  markRead(this: MessagesStoreWithOffline, chatId: string | number, messageIds: (string | number)[]) {
    if (!this.messagesByChat[chatId]) return;

    logger.log('Store: markRead called', { chatId, messageIds });

    const messages = this.messagesByChat[chatId];
    let updated = false;

    const updatedMessages = messages.map((message: NormalisedMessage) => {
      if (messageIds.includes(message.id)) {
        logger.log('Store: Updating message status', message.id, 'from', message.status, 'to read');
        updated = true;
        return {
          ...message,
          is_read: true,
          is_new: false,
          status: 'read',
        };
      }
      return message;
    });

    if (updated) {
      this.messagesByChat = {
        ...this.messagesByChat,
        [chatId]: updatedMessages
      };
    }

    this.unread[chatId] = 0;
    updateMessagesHeaderBadge(this.unread);
    this.updateSidebarBadgeState(chatId, 0);
  },

  // ===== Delivery Ack (реальная индикация доставки) =====

  ackDelivered(this: MessagesStoreWithOffline, chatId: string | number, messageIds: (string | number)[]) {
    const ids = (messageIds || []).map((id: string | number) => String(id)).filter(Boolean);
    if (!ids.length) return;

    // У WebSocketBootstrap нет метода send() — только sendMessage()
    const bootstrap = (window as any).wsBootstrap;
    const send = bootstrap?.sendMessage?.bind(bootstrap) || bootstrap?.send?.bind(bootstrap);
    if (!send) return;

    try {
      send({
        type: 'mark_delivered',
        channel: 'navigation',
        data: {
          action: 'mark_delivered',
          type: 'messages',
          contact_id: chatId,
          user_id: this.getCurrentUserId(),
          message_ids: ids,
        },
      });
      logger.log('Store: mark_delivered sent via WebSocket', { chatId, count: ids.length });
    } catch (e) {
      logger.warn('Store: mark_delivered send failed', e);
    }
  },

  async readConversation(this: MessagesStoreWithOffline, chatId: string | number) {
    const api = this.getApi();
    const currentUserId = this.getCurrentUserId();

    logger.log('Store: readConversation - marking conversation as read', { chatId, currentUserId });

    let wsSent = false;
    // У WebSocketBootstrap нет метода send() — только sendMessage()
    const bootstrap = (window as any).wsBootstrap;
    const wsSend = bootstrap?.sendMessage?.bind(bootstrap) || bootstrap?.send?.bind(bootstrap);
    if (wsSend) {
      try {
        logger.log('Store: Sending message_read via WebSocket');
        const result = wsSend({
          type: 'mark_read',
          channel: 'navigation',
          data: {
            action: 'mark_read',
            type: 'messages',
            contact_id: chatId,
            user_id: currentUserId,
          },
        });
        // sendMessage возвращает false, если соединение не установлено
        wsSent = result !== false;
        logger.log('Store: WebSocket message_read sent successfully');
      } catch (wsError) {
        logger.warn('Store: WebSocket send failed, falling back to HTTP', wsError);
      }
    }

    if (!wsSent && navigator.onLine) {
      try {
        await api.markRead({ chatId, messageIds: [] });
        logger.log('Store: HTTP markRead completed (fallback)');
      } catch (e) {
        logger.warn('Store: HTTP markRead failed', e);
      }
    } else if (!wsSent && !navigator.onLine) {
      // Queue for sync when back online
      if (this._offlineStorage) {
        await this._offlineStorage.queueOperation('send_message', {
          action: 'mark_read',
          chatId,
          userId: currentUserId,
        }, { priority: 'low' });
        logger.log('Store: markRead queued for offline sync');
      }
    }

    const list = this.messagesByChat[chatId] || [];
    const receivedIds = list.filter((m: NormalisedMessage) => m.type === 'received').map((m: NormalisedMessage) => m.id);
    if (receivedIds.length) {
      this.markRead(chatId, receivedIds);
    }
    this.unread[chatId] = 0;
    updateMessagesHeaderBadge(this.unread);
    this.updateSidebarBadgeState(chatId, 0);
  },

  handleMessageReadEvent(this: MessagesStoreWithOffline, data: MessageReadEventData) {
    const contactId = data.contact_id;
    if (!contactId) return;

    logger.log('Store: handleMessageReadEvent - marking messages as read for contact', contactId, data);

    // Сначала обновляем last_message в conversations (это всегда доступно)
    const convKey = String(contactId);
    const conv = this.conversations[convKey];
    if (conv?.last_message && conv.last_message.type === 'sent') {
      const now = Date.now();

      conv.last_message = {
        ...conv.last_message,
        status: 'read',
        is_read: true,
        read_at: now,
      };
      this.conversations[convKey] = { ...conv };

      const messageId = String(conv.last_message.id);
      const cacheKey = `${contactId}-${messageId}`;
      this.messageStatusesCache[cacheKey] = {
        status: 'read',
        is_read: true,
        timestamp: now,
      };

      updateConversationOrder(this, convKey, conv.last_message);
      
      logger.log('Store: Updated last_message status to read for contact', contactId);
    }

    // Затем обновляем сообщения в messagesByChat если диалог открыт
    const messages = this.messagesByChat[contactId] || [];
    const messageIds = messages.filter((m: NormalisedMessage) => m.type === 'sent' && m.status !== 'read').map((m: NormalisedMessage) => m.id);

    if (messageIds.length > 0) {
      logger.log('Store: Updating', messageIds.length, 'messages to read status');
      this.markRead(contactId, messageIds);
    } else {
      logger.log('Store: No sent messages to update in messagesByChat for contact', contactId);
    }
  },

  handleMessageDeliveredEvent(this: MessagesStoreWithOffline, data: any) {
    const contactId = data.contact_id;
    const deliveredIds: string[] = (data.message_ids || []).map(String);
    if (!contactId || !deliveredIds.length) return;

    logger.log('Store: handleMessageDeliveredEvent - marking messages delivered for contact', contactId, data);

    const deliveredAt = data.delivered_at ?? data.timestamp ?? undefined;
    const deliveredSet = new Set(deliveredIds);

    // last_message в conversations (для сайдбара)
    const convKey = String(contactId);
    const conv = this.conversations[convKey];
    const convMatches = conv?.last_message && conv.last_message.type === 'sent'
        && conv.last_message.status === 'sent'
        && (deliveredSet.has(String(conv.last_message.id))
            || (conv.last_message as any)._serverId !== undefined && deliveredSet.has(String((conv.last_message as any)._serverId)));
    if (convMatches) {
      conv.last_message = {
        ...conv.last_message,
        status: 'delivered',
        delivered_at: deliveredAt,
      };
      this.conversations[convKey] = { ...conv };
      updateConversationOrder(this, convKey, conv.last_message);
    }

    // Сообщения открытого диалога: только 'sent' → 'delivered' ('read' не понижаем)
    // Матчим и по id, и по _serverId: у отправителя сообщение сохраняет tempId в id,
    // а серверный ID лежит в _serverId (merge в addMessage); событие несёт серверные ID.
    const messages = this.messagesByChat[contactId] || [];
    const hasOpenChat = this.messagesByChat[contactId] !== undefined;
    if (hasOpenChat) {
      const matchesDelivery = (m: NormalisedMessage) =>
        deliveredSet.has(String(m.id)) ||
        (m._serverId !== undefined && m._serverId !== null && deliveredSet.has(String(m._serverId)));
      const updatedList = messages.map((m: NormalisedMessage) =>
        m.type === 'sent' && m.status === 'sent' && matchesDelivery(m)
          ? { ...m, status: 'delivered', delivered_at: deliveredAt }
          : m
      );
      this.messagesByChat[contactId] = updatedList;
      logger.log('Store: Updated', deliveredSet.size, 'messages to delivered status');
    }
  },

  setPresence(
    this: MessagesStoreWithOffline,
    userId: string | number,
    status: OnlineStatus,
    last_seen?: number | null,
    last_activity?: number | null,
  ) {
    this.presence[userId] = status;

    const convKey = String(userId);
    const conv = this.conversations[convKey];
    if (conv?.contact) {
      const contact = { ...conv.contact };
      contact.last_seen = last_seen ?? contact.last_seen ?? null;
      contact.last_activity = last_activity ?? contact.last_activity ?? null;
      // Серверный lastOnlineText устаревает при live-переходе в offline —
      // сбрасываем, чтобы хедер пересчитал по свежему timestamp
      if (last_seen) {
        delete (contact as Record<string, unknown>).lastOnlineText;
      }
      this.conversations[convKey] = { ...conv, contact };
    }
  },

  // ===== Message Editing =====

  async editMessage(
    this: MessagesStoreWithOffline,
    chatId: string | number,
    messageId: string | number,
    newText: string,
    apiMessageId?: string | number,
  ) {
    const api = this.getApi();

    try {
      const message = this.messagesByChat[chatId]?.find(
        m => String(m.id) === String(messageId)
      );
      // apiMessageId — серверный id для API-вызова (если optimistic ещё под tempId).
      // По умолчанию используем messageId (как было раньше).
      const resolvedApiId = apiMessageId !== undefined && apiMessageId !== null
        ? apiMessageId
        : (message?._serverId ?? messageId);

      logger.debug('Store: editMessage - found message:', message);

      if (!message) {
        logger.warn('Store: Message not found for editing:', messageId);
        return { success: false, error: 'Message not found' };
      }

      if (message.type !== 'sent') {
        logger.warn('Store: Cannot edit - message type is not sent:', message.type);
        return { success: false, error: 'Can only edit your own messages' };
      }

      if (message._deletedLocally) {
        logger.warn('Store: Cannot edit - message is deleted locally');
        return { success: false, error: 'Cannot edit deleted message' };
      }

      const originalText = message.text;

      logger.debug('Store: editMessage - optimistic update with text:', newText);

      this.updateMessage(messageId, {
        chatId,
        text: newText,
        _isEditing: true,
      });

      logger.debug('Store: editMessage - calling API', { resolvedApiId });
      const result = await api.editMessage({ messageId: resolvedApiId, text: newText });
      logger.debug('Store: editMessage - API result:', result);

      if (result.success) {
        const serverIsEdited = result.message?.is_edited;
        const finalIsEdited = serverIsEdited ?? true;
        
        logger.debug('Store: editMessage - updating with server data:', {
          text: result.message?.text || newText,
          is_edited: finalIsEdited,
          edited_at: result.message?.edited_at,
        });
        
        this.updateMessage(messageId, {
          chatId,
          text: result.message?.text || newText,
          is_edited: finalIsEdited,
          edited_at: result.message?.edited_at,
          _isEditing: false,
        });

        const convKey = String(chatId);
        const conv = this.conversations[convKey];
        if (conv?.last_message?.id == messageId) {
          conv.last_message!.text = result.message?.text || newText;
          conv.last_message!.is_edited = finalIsEdited;
          conv.last_message!.edited_at = result.message?.edited_at;
          this.conversations[convKey] = { ...conv };
        }

        return { success: true };
      } else {
        logger.warn('Store: editMessage - API error:', {
          error_code: result.error_code,
          message: result.message,
          errors: result.errors,
          fullResult: result
        });
        
        this.updateMessage(messageId, {
          chatId,
          text: originalText,
          _isEditing: false,
          _editError: result.message,
        });
        return { 
          success: false, 
          error: result.message || 'Edit failed',
          error_code: result.error_code,
          errors: result.errors
        };
      }
    } catch (error: any) {
      logger.error('Store: Edit message error:', error);
      return { success: false, error: error.message || 'Edit failed' };
    }
  },

  handleMessageEditedEvent(this: MessagesStoreWithOffline, data: any) {
    const { message_id, contact_id, new_text, is_edited, edited_at } = data;

    const chatId = contact_id;
    if (!this.messagesByChat[chatId]) return;

    const message = this.messagesByChat[chatId].find(
      m => String(m.id) === String(message_id)
    );

    if (message) {
      this.updateMessage(message_id, {
        chatId,
        text: new_text,
        is_edited: is_edited,
        edited_at: edited_at,
      });

      const convKey = String(chatId);
      const conv = this.conversations[convKey];
      if (conv?.last_message?.id == message_id) {
        conv.last_message!.text = new_text;
        conv.last_message!.is_edited = is_edited;
        this.conversations[convKey] = { ...conv };
      }

      if (window.messenger?.info) {
        window.messenger.info('Сообщение было изменено');
      }
    }
  },

  // ===== Message Deletion =====

  async deleteMessage(this: MessagesStoreWithOffline, chatId: string | number, messageId: string | number) {
    const api = this.getApi();
    const currentUserId = this.getCurrentUserId();

    logger.log('Store: deleteMessage called', { chatId, messageId });

    try {
      // Find message locally
      const message = this.messagesByChat[chatId]?.find(
        m => String(m.id) === String(messageId)
      );

      if (!message) {
        logger.warn('Store: Message not found for deletion:', messageId);
        return { success: false, error: 'Message not found' };
      }

      // Validation: only own messages or received messages can be deleted locally
      const isOwnMessage = String(message.from_user_id) === String(currentUserId);
      
      if (!isOwnMessage && message.type === 'sent') {
        return { success: false, error: 'Can only delete your own messages' };
      }

      // Use _serverId for API call if available (optimistic messages keep temp ID as id)
      const apiMessageId = message._serverId || message.id;

      // Optimistic delete from local store
      const originalList = this.messagesByChat[chatId] || [];
      const filteredList = originalList.filter(m => String(m.id) !== String(messageId));
      
      this.messagesByChat = {
        ...this.messagesByChat,
        [chatId]: filteredList
      };

      // Also delete from offline storage if present
      if (this._offlineStorage) {
        await this._offlineStorage.deleteMessage(String(messageId));
      }

      // API call to delete on server
      let result: any = null;
      let apiError: any = null;
      try {
        result = await api.deleteMessage({ messageIds: [apiMessageId] });
      } catch (error: any) {
        apiError = error;
        // Network error or offline - queue for sync
        if (!navigator.onLine || apiError?.message?.includes('network') || apiError?.message?.includes('fetch')) {
          logger.log('Store: Offline detected, queuing delete operation for sync');
          if (this._offlineStorage) {
            try {
              await this._offlineStorage.queueOperation('delete_message', {
                messageId: String(apiMessageId),
                chatId: String(chatId)
              }, { priority: 'high' });
              void this._offlineStorage.requestSync();
              // Message already removed from UI (optimistic delete), no need for visual indicator
              return { success: true, pending: true };
            } catch (queueError) {
              logger.error('Store: Failed to queue delete operation:', queueError);
            }
          }
          // If queuing fails, restore message
          this.messagesByChat = {
            ...this.messagesByChat,
            [chatId]: originalList
          };
          return { success: false, error: 'Offline and queue failed' };
        }
      }

      if (apiError) {
        // Other errors - restore message and return error
        this.messagesByChat = {
          ...this.messagesByChat,
          [chatId]: originalList
        };
        logger.error('Store: API error during delete:', apiError.message);
        return { success: false, error: apiError.message || 'Delete failed' };
      }

      if (!result?.success) {
        // Restore message on server error (not network error)
        this.messagesByChat = {
          ...this.messagesByChat,
          [chatId]: originalList
        };
        logger.error('Store: Failed to delete message on server:', result?.message);
        return { success: false, error: result?.message || 'Delete failed' };
      }

      // Update conversation last_message if needed
      const convKey = String(chatId);
      const conv = this.conversations[convKey];
      if (conv?.last_message?.id == messageId) {
        // Find new last message
        const newLastMessage = filteredList[filteredList.length - 1];
        if (newLastMessage) {
          conv.last_message = {
            id: newLastMessage.id,
            text: newLastMessage.text?.substring(0, 100),
            timestamp: newLastMessage.timestamp,
            type: newLastMessage.type,
            status: newLastMessage.status,
            is_read: newLastMessage.is_read,
            hasMedia: Array.isArray(newLastMessage.attachments) && newLastMessage.attachments.length > 0,
          };
        } else {
          conv.last_message = undefined;
        }
        this.conversations[convKey] = { ...conv };
      }

      logger.log('Store: Message deleted successfully:', messageId);
      return { success: true };
    } catch (error: any) {
      console.error('Store: Delete message error:', error);
      return { success: false, error: error.message || 'Delete failed' };
    }
  },

  async deleteMessages(this: MessagesStoreWithOffline, chatId: string | number, messageIds: (string | number)[]) {
    const api = this.getApi();
    const currentUserId = this.getCurrentUserId();

    logger.log('Store: deleteMessages called', { chatId, count: messageIds.length });

    try {
      // Filter to only own messages
      const messages = this.messagesByChat[chatId] || [];
      const deletableIds = messageIds.filter(id => {
        const msg = messages.find(m => String(m.id) === String(id));
        return msg && (String(msg.from_user_id) === String(currentUserId) || msg.type === 'received');
      });

      if (deletableIds.length === 0) {
        return { success: false, error: 'No messages can be deleted' };
      }

      // Optimistic delete
      const originalList = [...messages];
      const filteredList = messages.filter(m => !deletableIds.some(id => String(m.id) === String(id)));
      
      this.messagesByChat = {
        ...this.messagesByChat,
        [chatId]: filteredList
      };

      // Delete from offline storage
      if (this._offlineStorage) {
        for (const id of deletableIds) {
          await this._offlineStorage.deleteMessage(String(id));
        }
      }

      // Map to _serverId for API call (optimistic messages keep temp ID as id)
      const apiIds = deletableIds.map(id => {
        const msg = messages.find(m => String(m.id) === String(id));
        return msg?._serverId || id;
      });

      // API call
      let result: any = null;
      let apiError: any = null;
      try {
        result = await api.deleteMessage({ messageIds: apiIds });
      } catch (error: any) {
        apiError = error;
        // Network error or offline - queue for sync
        if (!navigator.onLine || apiError?.message?.includes('network') || apiError?.message?.includes('fetch')) {
          logger.log('Store: Offline detected, queuing bulk delete operations for sync');
          if (this._offlineStorage) {
            try {
              // Queue each delete operation individually
              for (const id of apiIds) {
                await this._offlineStorage.queueOperation('delete_message', {
                  messageId: String(id),
                  chatId: String(chatId)
                }, { priority: 'high' });
              }
              void this._offlineStorage.requestSync();
              // Messages already removed from UI (optimistic delete), no need for visual indicator
              return { success: true, pending: true, deletedCount: deletableIds.length };
            } catch (queueError) {
              logger.error('Store: Failed to queue bulk delete operations:', queueError);
            }
          }
          // If queuing fails, restore messages
          this.messagesByChat = {
            ...this.messagesByChat,
            [chatId]: originalList
          };
          return { success: false, error: 'Offline and queue failed' };
        }
      }

      if (apiError) {
        // Other errors - restore messages and return error
        this.messagesByChat = {
          ...this.messagesByChat,
          [chatId]: originalList
        };
        logger.error('Store: API error during bulk delete:', apiError.message);
        return { success: false, error: apiError.message || 'Bulk delete failed' };
      }

      if (!result?.success) {
        // Restore on server error (not network error)
        this.messagesByChat = {
          ...this.messagesByChat,
          [chatId]: originalList
        };
        return { success: false, error: result?.message || 'Bulk delete failed' };
      }

      // Update conversation last_message
      const convKey = String(chatId);
      const conv = this.conversations[convKey];
      const wasLastMessageDeleted = deletableIds.some(id => conv?.last_message?.id == id);
      
      if (wasLastMessageDeleted && conv) {
        const newLastMessage = filteredList[filteredList.length - 1];
        if (newLastMessage) {
          conv.last_message = {
            id: newLastMessage.id,
            text: newLastMessage.text?.substring(0, 100),
            timestamp: newLastMessage.timestamp,
            type: newLastMessage.type,
            status: newLastMessage.status,
            is_read: newLastMessage.is_read,
            hasMedia: Array.isArray(newLastMessage.attachments) && newLastMessage.attachments.length > 0,
          };
        } else {
          conv.last_message = undefined;
        }
        this.conversations[convKey] = { ...conv };
      }

      logger.log('Store: Messages deleted successfully:', deletableIds.length);
      return { success: true, deletedCount: deletableIds.length };
    } catch (error: any) {
      console.error('Store: Bulk delete error:', error);
      return { success: false, error: error.message || 'Bulk delete failed' };
    }
  },

  // ===== Reactions =====

  async toggleReaction(this: MessagesStoreWithOffline, chatId: string | number, messageId: string | number, emoji: string) {
    const api = this.getApi();
    const currentUserId = this.getCurrentUserId();

    logger.log('Store: toggleReaction called', { chatId, messageId, emoji });

    let message: NormalisedMessage | undefined;
    let apiMessageId: string | number = messageId;

    try {
      // Ищем сообщение по id или _serverId, чтобы реакция работала
      // даже для только что отправленных оптимистичных сообщений, у которых id ещё временный.
      message = this.messagesByChat[chatId]?.find(
        m => String(m.id) === String(messageId) || String(m._serverId) === String(messageId)
      );

      if (!message) {
        return { success: false, error: 'Message not found' };
      }

      // Для API используем реальный server id, если он уже известен.
      apiMessageId = message._serverId || message.id;

      // Get current reactions (grouped shape: { emoji, count, user_ids }[])
      const currentReactions = (message.reactions || []) as any[];

      const userIdStr = String(currentUserId);
      let newReactions: any[] = currentReactions
        .map((r: any) => ({
          ...r,
          count: Number(r.count || 0),
          user_ids: (r.user_ids || []).map((id: any) => String(id)),
        }))
        .filter((r: any) => r.count > 0 && r.emoji);

      // Remove user's existing reaction from any emoji group (change scenario)
      newReactions = newReactions.map((r: any) => {
        if (r.user_ids.includes(userIdStr)) {
          const nextUserIds = r.user_ids.filter((id: string) => id !== userIdStr);
          return nextUserIds.length > 0
            ? { ...r, count: nextUserIds.length, user_ids: nextUserIds }
            : null;
        }
        return r;
      }).filter(Boolean) as any[];

      // Toggle target emoji
      const targetGroup = newReactions.find((r: any) => r.emoji === emoji);
      if (targetGroup) {
        // User already removed above if they had this emoji, so adding
        targetGroup.user_ids.push(userIdStr);
        targetGroup.count = targetGroup.user_ids.length;
      } else {
        newReactions.push({ emoji, count: 1, user_ids: [userIdStr] });
      }

      this.updateMessage(messageId, {
        chatId,
        reactions: newReactions,
      });

      const isNetworkError = !navigator.onLine;

      if (isNetworkError && this._offlineStorage) {
        try {
          await this._offlineStorage.queueOperation('toggle_reaction', {
            messageId: String(messageId),
            emoji,
          }, { priority: 'normal' });
          void this._offlineStorage.requestSync();
        } catch (offlineErr) {
          console.warn('Store: Failed to queue reaction to offline storage:', offlineErr);
        }

        return { success: true, reactions: newReactions, method: 'offline-queued' };
      }

      // API call использует реальный server id (или temp id, если server id ещё неизвестен).
      const result = await api.toggleReaction({ messageId: apiMessageId, emoji });

      if (!result.success) {
        // Rollback on failure
        this.updateMessage(messageId, {
          chatId,
          reactions: currentReactions,
        });
        return { success: false, error: result.message || 'Toggle reaction failed' };
      }

      // Update with server response
      if (result.reactions) {
        this.updateMessage(messageId, {
          chatId,
          reactions: result.reactions,
        });
      }

      return { success: true, reactions: result.reactions || newReactions };
    } catch (error: any) {
      console.error('Store: Toggle reaction error:', error);

      const isNetworkError =
        !navigator.onLine || (error instanceof TypeError && /fetch|network/i.test((error as TypeError).message));

      if (isNetworkError && this._offlineStorage) {
        try {
          await this._offlineStorage.queueOperation('toggle_reaction', {
            messageId: String(apiMessageId),
            emoji,
          }, { priority: 'normal' });
          void this._offlineStorage.requestSync();

          const message = this.messagesByChat[chatId]?.find(
            m => String(m.id) === String(messageId) || String(m._serverId) === String(messageId)
          );
          const userIdStr = String(currentUserId);
          let currentReactions = ((message?.reactions || []) as any[])
            .map((r: any) => ({
              ...r,
              count: Number(r.count || 0),
              user_ids: (r.user_ids || []).map((id: any) => String(id)),
            }))
            .filter((r: any) => r.count > 0 && r.emoji);

          currentReactions = currentReactions.map((r: any) => {
            if (r.user_ids.includes(userIdStr)) {
              const nextUserIds = r.user_ids.filter((id: string) => id !== userIdStr);
              return nextUserIds.length > 0
                ? { ...r, count: nextUserIds.length, user_ids: nextUserIds }
                : null;
            }
            return r;
          }).filter(Boolean) as any[];

          const targetGroup = currentReactions.find((r: any) => r.emoji === emoji);
          if (targetGroup) {
            targetGroup.user_ids.push(userIdStr);
            targetGroup.count = targetGroup.user_ids.length;
          } else {
            currentReactions.push({ emoji, count: 1, user_ids: [userIdStr] });
          }

          this.updateMessage(messageId, { chatId, reactions: currentReactions });
          return { success: true, reactions: currentReactions, method: 'offline-queued' };
        } catch (offlineErr) {
          console.warn('Store: Failed to queue reaction to offline storage:', offlineErr);
        }
      }

      return { success: false, error: error.message || 'Toggle reaction failed' };
    }
  },

  // ===== Retry Failed Messages =====

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async retryFailedMessage(this: MessagesStoreWithOffline, chatId: string | number, messageId: string | number) {
    const api = this.getApi();

    logger.log('Store: retryFailedMessage called', { chatId, messageId });

    try {
      const message = this.messagesByChat[chatId]?.find(
        m => String(m.id) === String(messageId)
      );

      if (!message) {
        return { success: false, error: 'Message not found' };
      }

      if (message.status !== 'failed' && message.status !== 'error' && !message._offline) {
        return { success: false, error: 'Message is not in failed state' };
      }

      // Медиа-сообщение: восстанавливаем File из blob-превью (objectUrl живы,
      // пока отправка не успешна) и отправляем заново через полный sendMessage
      // (с прогрессом и корректным текстом сопроводительного сообщения)
      if (message.attachments?.length) {
        const files: File[] = [];
        for (const att of message.attachments as any[]) {
          if (!att.objectUrl) continue;
          try {
            const blob = await (await fetch(att.objectUrl)).blob();
            files.push(new File([blob], att.name || 'photo.jpg', { type: blob.type || 'image/jpeg' }));
          } catch {
            // blob уже отозван — восстановить нельзя
          }
        }
        if (files.length === 0) {
          window.messenger?.error?.('Вложения недоступны для повтора — отправьте сообщение заново');
          return { success: false, error: 'Attachments no longer available' };
        }

        const text = (message.text as string) || '';
        const list = this.messagesByChat[chatId] || [];
        this.messagesByChat[chatId] = list.filter((m) => m !== message);
        await this.sendMessage({ text, contactId: chatId, files });
        return { success: true };
      }

      // Update status to pending
      this.updateMessage(messageId, {
        chatId,
        status: 'pending',
        _pending: true,
      });

      // Retry sending
      const result = await api.sendMessage({
        text: message.text,
        contactId: chatId,
        tempId: messageId,
      });

      if (result.success) {
        this.updateMessage(messageId, {
          chatId,
          status: 'sent',
          _pending: false,
          _serverId: result.messageId ?? undefined,
        });

        // Remove from offline storage if present
        if (this._offlineStorage && message._offline && result.messageId) {
          await this._offlineStorage.markMessageSynced(String(messageId), String(result.messageId));
        }

        logger.log('Store: Message retry successful:', messageId);
        return { success: true, messageId: result.messageId };
      } else {
        this.updateMessage(messageId, {
          chatId,
          status: 'failed',
          _pending: false,
        });
        return { success: false, error: result.message || 'Retry failed' };
      }
    } catch (error: any) {
      console.error('Store: Retry message error:', error);
      
      // Mark as failed again
      this.updateMessage(messageId, {
        chatId,
        status: 'failed',
        _pending: false,
      });
      
      return { success: false, error: error.message || 'Retry failed' };
    }
  },

  // ===== Pagination =====

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async loadMoreMessages(this: MessagesStoreWithOffline, chatId: string | number, beforeId: string | number) {
    const api = this.getApi();

    logger.log('Store: loadMoreMessages called', { chatId, beforeId });

    try {
      this.loadingMore = true;

      const response = await api.getMessages(chatId, beforeId);

      const messages = (response.messages || []) as any[];
      const normalized = messages.map((m: any) => normalizeMessage(m)).filter((m): m is NonNullable<typeof m> => m !== null) as NormalisedMessage[];

      // Prepend to existing messages
      const currentList = this.messagesByChat[chatId] || [];
      const mergedResult = mergeMessagesWithDedup(normalized, currentList);
      this.messagesByChat[chatId] = (mergedResult as any).merged || mergedResult;

      this.hasMore = !!(response.hasMore ?? (messages.length === 50));

      logger.log('Store: Loaded', normalized.length, 'more messages, hasMore:', this.hasMore);
      return { success: true, count: normalized.length, hasMore: this.hasMore };
    } catch (error: any) {
      console.error('Store: Load more messages error:', error);
      return { success: false, error: error.message || 'Failed to load more messages' };
    } finally {
      this.loadingMore = false;
    }
  },

  // ===== Store Lifecycle =====

  $dispose(this: MessagesStoreWithOffline) {
    this.cleanupObjectUrls();
    this.conversations = {};
    this.messagesByChat = {};
    this.typing = {};
    this.unread = {};
    this.presence = {};
    this.messageStatusesCache = {};
    this.pendingAcks = {};
    this.groupedCache = {};
  },

  async refresh(this: MessagesStoreWithOffline) {
    await this.loadConversations();
  },
});

// NOTE: The following functions are intentionally kept as they are available for future UI features:
// - loadDraft (line 696)
// - clearDraft (line 705) 
// - retryFailedMessage (line 1416)
// - loadMoreMessages (line 1488)
// These functions provide complete offline messaging functionality and can be used when the UI needs them.
